
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function Approach() {
  const { ref: sectionRef, isVisible } = useScrollAnimation({ threshold: 0.1 });

  const experiences = [
    {
      period: '2024.11 — 2025.6',
      duration: '8 個月',
      title: '法務專員',
      company: '律師事務所',
      type: '全職',
      current: false,
      tags: ['訴訟文書', '合約審閱', '法律諮詢', '法律研究'],
      points: [
        '草擬訴訟文書、審約，處理一般法律諮詢事項',
        '執行法律資料檢索，支援案件分析與決策準備',
        '協助律師準備開庭文件及法律意見書',
        '整理判決資料，建立案件參考資料庫',
      ],
      icon: 'ri-scales-3-line',
      color: 'from-orange-500 to-rose-500',
    },
    {
      period: '2022.12 — 2024.10',
      duration: '1 年 10 個月',
      title: '行政秘書',
      company: '律師事務所',
      type: '全職',
      current: false,
      tags: ['案件流程管理', '文件標準化', '跨機關協調', '行政管理'],
      points: [
        '負責訴訟案件流程管理，確保案件作業時效與準確性',
        '法律文件標準化整理，優化文件格式與作業流程',
        '與法院、地政及戶政等機關溝通協作，追蹤處理進度',
        '運用 Excel 建立案件追蹤表與費用管理報表',
      ],
      icon: 'ri-briefcase-3-line',
      color: 'from-rose-400 to-rose-600',
    },
  ];

  return (
    <section className="py-16 md:py-24 px-4 md:px-8 bg-gradient-to-br from-gray-50 to-orange-50" ref={sectionRef}>
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="text-center mb-10 md:mb-16">
          <div
            className={`inline-block px-4 py-2 bg-orange-100 rounded-full mb-6 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          >
            <span className="text-sm font-semibold text-orange-700">工作經歷</span>
          </div>
          <h2
            className={`text-3xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ fontFamily: 'Playfair Display, serif', transitionDelay: '100ms' }}
          >
            我的職涯歷程
          </h2>
          <p
            className={`text-base text-gray-600 max-w-2xl mx-auto transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
            style={{ transitionDelay: '200ms' }}
          >
            從法律領域出發，以嚴謹的分析思維邁向數據世界
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto relative">
          {/* Vertical line */}
          <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-orange-300 via-rose-200 to-gray-200"></div>

          <div className="space-y-10">
            {experiences.map((exp, i) => (
              <div
                key={i}
                className={`relative pl-20 transition-all duration-700 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
                style={{ transitionDelay: `${300 + i * 200}ms` }}
              >
                {/* Dot */}
                <div className={`absolute left-0 top-1 w-12 h-12 bg-gradient-to-br ${exp.color} rounded-xl flex items-center justify-center shadow-lg`}>
                  <i className={`${exp.icon} text-white text-xl`}></i>
                </div>

                {/* Card */}
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-shadow duration-300">
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-gray-900 mb-1" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {exp.title}
                      </h3>
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <i className="ri-building-line text-orange-400"></i>
                        <span className="font-medium">{exp.company}</span>
                        <span>·</span>
                        <span>{exp.type}</span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-center gap-2 justify-end mb-1">
                        {exp.current && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                            <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
                            在職中
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">{exp.period}</p>
                      <p className="text-xs font-semibold text-orange-600">{exp.duration}</p>
                    </div>
                  </div>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {exp.tags.map((tag, j) => (
                      <span key={j} className="px-3 py-1 bg-orange-50 text-orange-700 text-xs font-semibold rounded-full border border-orange-100">
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* Points */}
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                    {exp.points.map((point, j) => (
                      <li
                        key={j}
                        className={`flex items-start gap-2 text-sm text-gray-600 transition-all duration-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-4'}`}
                        style={{ transitionDelay: `${300 + i * 200 + 200 + j * 60}ms` }}
                      >
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        {point}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div
          className={`text-center mt-14 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
          style={{ transitionDelay: '800ms' }}
        >
          <Link
            to="/experience"
            className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-semibold text-base bg-gradient-to-r from-orange-500 to-rose-500 text-white hover:shadow-xl hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            查看完整經歷與學歷
            <i className="ri-arrow-right-line text-lg"></i>
          </Link>
        </div>

      </div>
    </section>
  );
}
